import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { adminApi, commentsApi } from '../lib/api';
import { Shield, ArrowLeft, Trash2, Flag, Loader2, MessageCircle, Users, Languages, BarChart3, Euro } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export const AdminPanel = ({ password, onBack }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('stats');
  const [gems, setGems] = useState([]);
  const [comments, setComments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeTab === 'gems') loadReportedGems();
    else if (activeTab === 'comments') loadReportedComments();
    else if (activeTab === 'stats') loadStats();
  }, [activeTab]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getStats(password);
      setStats(res.data);
    } catch (err) {
      toast.error(t('failed_load_stats') || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const loadReportedGems = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getReportedGems(password);
      setGems(res.data.gems || []);
    } catch (err) {
      toast.error(t('failed_load_reported') || 'Failed to load reported gems');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (gemId) => {
    try {
      await adminApi.deleteGem(gemId, password);
      setGems(prev => prev.filter(g => g.id !== gemId));
      toast.success(t('gem_deleted_admin') || 'Gem deleted by admin');
    } catch (err) {
      toast.error(t('failed_delete_gem') || 'Failed to delete gem');
    }
  };

  const loadReportedComments = async () => {
    try {
      setLoading(true);
      const res = await commentsApi.getReportedComments(password);
      setComments(res.data.comments || []);
    } catch (err) {
      toast.error(t('failed_load_reported') || 'Failed to load reported comments');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await commentsApi.deleteComment(commentId, password);
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success(t('comment_deleted') || 'Comment deleted by admin');
    } catch (err) {
      toast.error(t('failed_delete') || 'Failed to delete comment');
    }
  };

  const formatMonth = (m) => {
    const [year, month] = m.split('-');
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[parseInt(month) - 1]} ${year}`;
  };

  const formatChars = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const tabs = [
    { key: 'stats', icon: BarChart3, label: t('admin_stats') || 'Statistics' },
    { key: 'gems', icon: Flag, label: `${t('reported_gems') || 'Reported Gems'} (${gems.length})` },
    { key: 'comments', icon: MessageCircle, label: `${t('reported_comments') || 'Comments'} (${comments.length})` },
  ];

  return (
    <div className="px-4 pt-4 pb-4">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-accent transition-colors duration-150"
          data-testid="admin-back-button"
        >
          <ArrowLeft className="w-4 h-4 text-foreground" />
        </button>
        <h1 className="text-xl font-[var(--font-heading)] tracking-[-0.01em] text-foreground flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          Admin Panel
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors relative whitespace-nowrap ${
              activeTab === tab.key ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`admin-tab-${tab.key}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
            {activeTab === tab.key && (
              <motion.div layoutId="adminTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      ) : activeTab === 'stats' ? (
        /* ========= STATISTICS TAB ========= */
        stats ? (
          <div className="space-y-4" data-testid="admin-stats-panel">
            {/* Current Month Summary Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-card p-4" data-testid="stat-visitors-current">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                    {t('visitors_this_month') || 'Visitors this month'}
                  </span>
                </div>
                <p className="text-2xl font-bold text-foreground">{stats.current_visitors}</p>
                <p className="text-[10px] text-muted-foreground">{formatMonth(stats.current_month)}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4" data-testid="stat-revenue-current">
                <div className="flex items-center gap-2 mb-2">
                  <Euro className="w-4 h-4 text-amber-500" />
                  <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                    {t('ad_revenue') || 'Ad Revenue (est.)'}
                  </span>
                </div>
                <p className="text-2xl font-bold text-foreground">€{stats.ad_revenue?.current_month_revenue_eur || '0.00'}</p>
                <p className="text-[10px] text-muted-foreground">{stats.ad_revenue?.current_month_impressions || 0} impressions</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-card p-4" data-testid="stat-translation-current">
                <div className="flex items-center gap-2 mb-2">
                  <Languages className="w-4 h-4 text-green-500" />
                  <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                    {t('chars_translated') || 'Chars translated'}
                  </span>
                </div>
                <p className="text-2xl font-bold text-foreground">{formatChars(stats.current_translation_chars)}</p>
                <p className="text-[10px] text-muted-foreground">{stats.current_translation_requests} {t('requests') || 'requests'}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-red-500" />
                  <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">DDoS</span>
                </div>
                <p className="text-2xl font-bold text-foreground">{stats.ddos_protection?.active_tracked_ips || 0}</p>
                <p className="text-[10px] text-muted-foreground">{stats.ddos_protection?.banned_ips || 0} {t('banned') || 'banned'}</p>
              </div>
            </div>

            {/* Visitor History */}
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="text-xs font-semibold text-foreground mb-3 flex items-center gap-2">
                <Users className="w-3.5 h-3.5 text-blue-500" />
                {t('visitor_history') || 'Monthly Visitors'}
              </h3>
              <div className="space-y-2">
                {stats.visitor_history.map((item) => {
                  const maxVal = Math.max(...stats.visitor_history.map(v => v.unique_visitors), 1);
                  const pct = (item.unique_visitors / maxVal) * 100;
                  return (
                    <div key={item.month} className="flex items-center gap-3">
                      <span className="text-[10px] text-muted-foreground w-14 shrink-0">{formatMonth(item.month)}</span>
                      <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.max(pct, 2)}%` }}
                          transition={{ duration: 0.5, delay: 0.1 }}
                          className="h-full bg-blue-500/70 rounded-full"
                        />
                      </div>
                      <span className="text-xs font-semibold text-foreground w-8 text-right">{item.unique_visitors}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Translation History */}
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="text-xs font-semibold text-foreground mb-3 flex items-center gap-2">
                <Languages className="w-3.5 h-3.5 text-green-500" />
                {t('translation_history') || 'Monthly Translation (chars)'}
              </h3>
              <div className="space-y-2">
                {stats.translation_history.map((item) => {
                  const maxVal = Math.max(...stats.translation_history.map(v => v.total_chars), 1);
                  const pct = (item.total_chars / maxVal) * 100;
                  return (
                    <div key={item.month} className="flex items-center gap-3">
                      <span className="text-[10px] text-muted-foreground w-14 shrink-0">{formatMonth(item.month)}</span>
                      <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.max(pct, 2)}%` }}
                          transition={{ duration: 0.5, delay: 0.1 }}
                          className="h-full bg-green-500/70 rounded-full"
                        />
                      </div>
                      <span className="text-xs font-semibold text-foreground w-12 text-right">{formatChars(item.total_chars)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ad Revenue History */}
            {stats.ad_revenue_history && (
              <div className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-xs font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Euro className="w-3.5 h-3.5 text-amber-500" />
                  {t('ad_revenue_history') || 'Monthly Ad Revenue (€)'}
                </h3>
                <div className="space-y-2">
                  {stats.ad_revenue_history.map((item) => {
                    const maxVal = Math.max(...stats.ad_revenue_history.map(v => v.estimated_revenue_eur), 0.01);
                    const pct = (item.estimated_revenue_eur / maxVal) * 100;
                    return (
                      <div key={item.month} className="flex items-center gap-3">
                        <span className="text-[10px] text-muted-foreground w-14 shrink-0">{formatMonth(item.month)}</span>
                        <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.max(pct, 2)}%` }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="h-full bg-amber-500/70 rounded-full"
                          />
                        </div>
                        <span className="text-xs font-semibold text-foreground w-12 text-right">€{item.estimated_revenue_eur}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[9px] text-muted-foreground mt-2 text-center">{stats.ad_revenue?.note || 'Estimated'}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground text-sm">No stats available</div>
        )
      ) : activeTab === 'gems' ? (
        gems.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-accent/50 flex items-center justify-center mx-auto mb-4">
              <Flag className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">No reported gems</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-reported-table">
          {gems.map((gem) => (
            <motion.div key={gem.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm text-foreground/90 leading-6">{gem.text}</p>
                  {gem.verse_ref && (
                    <span className="inline-block mt-1 text-xs text-accent-foreground bg-accent px-2 py-0.5 rounded-full">{gem.verse_ref}</span>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-muted-foreground">Reports: <strong className="text-destructive">{gem.reports_count}</strong></span>
                    <span className="text-xs text-muted-foreground">Likes: {gem.likes_count}</span>
                  </div>
                  {gem.report_reasons?.length > 0 && (
                    <div className="mt-2">
                      {gem.report_reasons.map((r, i) => (<p key={i} className="text-xs text-muted-foreground italic">"{r.reason}"</p>))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(gem.id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-xs font-medium hover:bg-destructive/20 transition-colors duration-200"
                  data-testid={`admin-delete-gem-${gem.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />{t('delete')}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
        )
      ) : (
        comments.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-accent/50 flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">No reported comments</p>
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map((comment) => (
              <motion.div key={comment.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-foreground">{comment.author_username || 'Anonymous'}</p>
                    <p className="text-xs text-muted-foreground">on gem: {comment.gem_verse_ref || 'Unknown'}</p>
                    <p className="text-sm text-foreground/90 leading-6 my-2">{comment.text}</p>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground"><Flag className="w-3 h-3" />{comment.reports_count} reports</span>
                  </div>
                  <button
                    onClick={() => handleDeleteComment(comment.id)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-destructive hover:bg-destructive/10 transition-colors"
                    data-testid={`admin-delete-comment-${comment.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />{t('delete')}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default AdminPanel;
